import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, Lock, Flame, Clock, Target, ArrowLeft, Send, Lightbulb,
  BookOpen, CheckCircle2, XCircle, Loader2, Brain, GraduationCap
} from 'lucide-react';

const API_URL = window.location.origin;
const token = () => localStorage.getItem('familyToken');

const call = async (path, { method = 'GET', body } = {}) => {
  const sep = path.includes('?') ? '&' : '?';
  const res = await fetch(`${API_URL}/api/dna-estudos${path}${sep}token=${token()}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Falha na requisição');
  return data;
};

const clean = (t) => String(t || '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-violet-100 bg-white p-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-violet-600">
      <Icon className="w-4 h-4" />
      <span className="text-[11px] uppercase tracking-wide">{label}</span>
    </div>
    <p className="text-xl font-bold text-violet-950 mt-1">{value}</p>
  </div>
);

/** Card do módulo — usado na home do DNA Connect e no topo desta página */
export const EstudosCard = ({ studentId, onOpen }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!studentId) return;
    call(`/status/${studentId}`).then(setStatus).catch(() => setStatus({ allowed: false, state: 'nao_liberado' }));
  }, [studentId]);
  if (!status) return null;

  if (!status.allowed) {
    return (
      <Card className="border-slate-200 bg-slate-50" data-testid="estudos-card-bloqueado">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-slate-200 flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <p className="font-semibold text-slate-700">DNA IA Estudos</p>
            <p className="text-xs text-slate-500">
              {status.student_name ? `${status.student_name.split(' ')[0]} · seu reforço inteligente, todos os dias.` : 'Seu reforço inteligente, todos os dias.'}
            </p>
            <p className="text-xs text-slate-600 mt-2">
              {status.message || 'O DNA IA Estudos ainda não está disponível para este aluno.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const ov = status.overview || {};
  return (
    <Card className="border-violet-200" data-testid="estudos-card-liberado">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-violet-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-violet-950">DNA IA Estudos</p>
            <p className="text-xs text-slate-500">
              {status.student_name ? `${status.student_name.split(' ')[0]} · seu reforço inteligente, todos os dias.` : 'Seu reforço inteligente, todos os dias.'}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat icon={Target} label="Progresso" value={`${ov.overall_progress ?? 0}%`} testid="estudos-progresso" />
          <Stat icon={Flame} label="Sequência" value={`${ov.streak_days ?? 0} d`} />
          <Stat icon={Clock} label="Semana" value={`${ov.minutes_week ?? 0} min`} />
          <Stat icon={GraduationCap} label="Dominadas" value={ov.skills_mastered ?? 0} />
        </div>
        {status.next_item && (
          <p className="text-xs text-slate-600">
            Próxima atividade: <strong>{status.next_item.titulo}</strong>
          </p>
        )}
        <Button className="w-full" onClick={onOpen} data-testid="estudos-continuar">
          Continuar estudando
        </Button>
      </CardContent>
    </Card>
  );
};

export default function DnaConnectEstudos() {
  const [filhos, setFilhos] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [home, setHome] = useState(null);
  const [view, setView] = useState('home');
  const [aula, setAula] = useState(null);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [hints, setHints] = useState({});
  const [diag, setDiag] = useState(null);
  const [tutorMsgs, setTutorMsgs] = useState([]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/dnaconnect/filhos?token=${token()}`)
      .then(r => r.json())
      .then(d => {
        const list = d.filhos || d.items || d || [];
        setFilhos(list);
        if (list.length) setStudentId(list[0].id);
      })
      .catch(() => {});
  }, []);

  const loadHome = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      setHome(await call(`/aluno/${studentId}/home`));
    } catch (e) {
      setHome({ blocked: e.message });
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { loadHome(); }, [loadHome]);

  // registro de tempo de estudo (a cada 60s dentro de uma aula)
  useEffect(() => {
    if (view !== 'aula' || !studentId) return;
    timer.current = setInterval(() => {
      call(`/aluno/${studentId}/tempo`, { method: 'POST', body: { minutes: 1, content_id: aula?.content_id } }).catch(() => {});
    }, 60000);
    return () => clearInterval(timer.current);
  }, [view, studentId, aula]);

  const openAula = async (contentId) => {
    setLoading(true);
    try {
      const data = await call(`/aluno/${studentId}/aula/${contentId}`);
      setAula(data);
      setAnswers({}); setFeedback({}); setHints({});
      setTutorMsgs([]);
      setView('aula');
      call(`/aluno/${studentId}/tempo`, { method: 'POST', body: { minutes: 1, content_id: contentId } }).catch(() => {});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const responder = async (q) => {
    if (!answers[q.id]) return toast.error('Escolha uma alternativa');
    try {
      const res = await call(`/aluno/${studentId}/responder`, {
        method: 'POST',
        body: { content_id: aula.content_id, question_id: q.id, answer: answers[q.id], hints_used: hints[q.id]?.count || 0 },
      });
      setFeedback(f => ({ ...f, [q.id]: res }));
      setAula(a => ({ ...a, mastery: res.mastery }));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const pedirDica = async (q) => {
    const cur = hints[q.id]?.count || 0;
    try {
      const res = await call(`/aluno/${studentId}/dica`, {
        method: 'POST', body: { content_id: aula.content_id, question_id: q.id, hint_index: cur },
      });
      setHints(h => ({ ...h, [q.id]: { count: cur + 1, text: res.dica } }));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const iniciarDiagnostico = async () => {
    setLoading(true);
    try {
      setDiag(await call(`/aluno/${studentId}/diagnostico/iniciar`, { method: 'POST' }));
      setView('diagnostico');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const responderDiag = async (alt) => {
    try {
      const res = await call(`/aluno/${studentId}/diagnostico/responder`, {
        method: 'POST',
        body: { content_id: diag.content_id, question_id: diag.question.id, answer: alt },
      });
      if (res.next?.status === 'concluido') {
        toast.success('Diagnóstico concluído! Sua trilha foi criada.');
        setDiag(null);
        setView('home');
        loadHome();
      } else {
        setDiag(res.next);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const perguntarTutor = async () => {
    const q = tutorInput.trim();
    if (!q) return;
    setTutorInput('');
    setTutorMsgs(m => [...m, { role: 'user', content: q }]);
    setTutorLoading(true);
    try {
      const res = await call(`/aluno/${studentId}/tutor`, {
        method: 'POST', body: { content_id: aula?.content_id, question: q },
      });
      setTutorMsgs(m => [...m, { role: 'assistant', content: res.answer }]);
    } catch (e) {
      setTutorMsgs(m => [...m, { role: 'assistant', content: e.message }]);
    } finally {
      setTutorLoading(false);
    }
  };

  if (!studentId) {
    return <p className="p-6 text-sm text-slate-500" data-testid="estudos-sem-aluno">Nenhum aluno vinculado a este acesso.</p>;
  }

  if (home?.blocked) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="estudos-bloqueado">
        <EstudosCard studentId={studentId} onOpen={loadHome} />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8" data-testid="dna-connect-estudos">
      {filhos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {filhos.map(f => (
            <Button key={f.id} size="sm" variant={studentId === f.id ? 'default' : 'outline'}
                    onClick={() => { setStudentId(f.id); setView('home'); }}
                    data-testid={`estudos-filho-${f.id}`}>
              {(f.name || '').split(' ')[0]}
            </Button>
          ))}
        </div>
      )}

      {view !== 'home' && (
        <Button variant="ghost" size="sm" onClick={() => { setView('home'); loadHome(); }} data-testid="estudos-voltar">
          <ArrowLeft className="w-4 h-4 mr-1" />Voltar
        </Button>
      )}

      {loading && <p className="text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Carregando…</p>}

      {/* ── HOME ─────────────────────────── */}
      {view === 'home' && home && (
        <>
          <div className="rounded-2xl bg-violet-700 p-5 text-white">
            <h1 className="text-2xl font-bold">Olá, {home.student?.first_name}!</h1>
            <p className="text-violet-200 text-sm">Seu reforço inteligente, todos os dias.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat icon={Target} label="Progresso" value={`${home.overview?.overall_progress ?? 0}%`} testid="estudos-home-progresso" />
            <Stat icon={Flame} label="Sequência" value={`${home.overview?.streak_days ?? 0} ${(home.overview?.streak_days ?? 0) === 1 ? 'dia' : 'dias'}`} />
            <Stat icon={Clock} label="Semana" value={`${home.overview?.minutes_week ?? 0}/${home.weekly_goal_minutes} min`} />
            <Stat icon={GraduationCap} label="Dominadas" value={home.overview?.skills_mastered ?? 0} />
          </div>

          {!home.has_content && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 text-sm text-amber-800" data-testid="estudos-sem-conteudo">
                Sua escola está preparando os conteúdos da sua série. Em breve novas aulas aparecerão aqui.
              </CardContent>
            </Card>
          )}

          {home.needs_diagnostic && home.has_content && (
            <Card className="border-violet-200">
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Brain className="w-8 h-8 text-violet-600" />
                <div className="flex-1">
                  <p className="font-semibold text-violet-950">Vamos descobrir onde você está indo bem</p>
                  <p className="text-xs text-slate-600">Um diagnóstico curto cria sua trilha personalizada.</p>
                </div>
                <Button onClick={iniciarDiagnostico} data-testid="estudos-iniciar-diagnostico">Começar diagnóstico</Button>
              </CardContent>
            </Card>
          )}

          {home.continuar && (
            <Card className="border-emerald-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-xs uppercase text-emerald-700 tracking-wide">Continuar de onde parei</p>
                  <p className="font-semibold">{home.continuar.titulo}</p>
                </div>
                <Button onClick={() => openAula(home.continuar.content_id)} data-testid="estudos-continuar-trilha">Continuar</Button>
              </CardContent>
            </Card>
          )}

          {home.recomendados?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Recomendações da IA</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {home.recomendados.map(r => (
                  <button key={r.content_id} onClick={() => openAula(r.content_id)}
                          className="w-full text-left border rounded-xl p-3 hover:border-violet-300 hover:bg-violet-50 transition-colors"
                          data-testid={`estudos-rec-${r.content_id}`}>
                    <p className="font-medium text-sm">{r.titulo}</p>
                    <p className="text-xs text-slate-500">{r.componente} · {r.skill_code} · {r.motivo}</p>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {home.habilidades_em_desenvolvimento?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Habilidades em desenvolvimento</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {home.habilidades_em_desenvolvimento.map(h => (
                  <div key={h.skill_code} className="flex items-center gap-2 text-sm">
                    <span className="w-24 font-mono text-xs text-violet-800">{h.skill_code}</span>
                    <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden">
                      <div className="h-full bg-violet-500" style={{ width: `${h.mastery || 0}%` }} />
                    </div>
                    <span className="w-10 text-right text-xs">{h.mastery || 0}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── DIAGNÓSTICO ─────────────────────────── */}
      {view === 'diagnostico' && diag?.question && (
        <Card data-testid="estudos-diagnostico">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Diagnóstico · questão {diag.step} de {diag.total}</CardTitle>
            <p className="text-xs text-slate-500">{diag.componente} · {diag.skill_code}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">{diag.question.enunciado}</p>
            {(diag.question.alternativas || []).map((alt, i) => (
              <Button key={i} variant="outline" className="w-full justify-start text-left h-auto py-2 whitespace-normal"
                      onClick={() => responderDiag(alt)} data-testid={`diag-alt-${i}`}>
                {alt}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── AULA ─────────────────────────── */}
      {view === 'aula' && aula && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-lg">{aula.package?.titulo}</CardTitle>
                  <p className="text-xs text-slate-500">{aula.skill?.componente} · {aula.skill?.codigo}</p>
                </div>
                <Badge className="bg-violet-100 text-violet-800 border-violet-200">Domínio {aula.mastery || 0}%</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="rounded-lg bg-violet-50 border border-violet-100 p-3">
                <strong>Objetivo:</strong> {aula.package?.objetivo}
              </p>
              {(aula.package?.explicacao || []).map((b, i) => (
                <div key={i} className="border-l-2 border-violet-200 pl-3" data-testid={`aula-bloco-${i}`}>
                  {b.titulo && <p className="font-semibold text-violet-900">{b.titulo}</p>}
                  <p className="text-slate-700 leading-relaxed">{b.texto}</p>
                </div>
              ))}
              {aula.package?.resumo && (
                <p className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                  <strong>Resumo:</strong> {aula.package.resumo}
                </p>
              )}
              {(aula.package?.exemplos || []).map((ex, i) => (
                <div key={i} className="rounded-lg border p-3">
                  <p className="font-medium">Exemplo {i + 1}</p>
                  <p className="text-slate-700">{ex.enunciado}</p>
                  <ol className="list-decimal ml-5 text-slate-600 mt-1">
                    {(ex.passos || []).map((p, j) => <li key={j}>{p}</li>)}
                  </ol>
                  {ex.resposta && <p className="mt-1 text-emerald-700"><strong>Resposta:</strong> {ex.resposta}</p>}
                </div>
              ))}
              {(aula.package?.flashcards || []).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {aula.package.flashcards.map((f, i) => (
                    <details key={i} className="rounded-lg border p-2.5">
                      <summary className="cursor-pointer font-medium">{f.frente}</summary>
                      <p className="text-slate-700 mt-1">{f.verso}</p>
                    </details>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" />Praticar</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {(aula.package?.questoes || []).map((q, idx) => (
                <div key={q.id} className="rounded-xl border p-3 space-y-2" data-testid={`aula-questao-${q.id}`}>
                  <p className="text-xs uppercase text-slate-400">{q.nivel}</p>
                  <p className="text-sm font-medium">{idx + 1}. {q.enunciado}</p>
                  <div className="space-y-1.5">
                    {(q.alternativas || []).map((alt, i) => (
                      <label key={i} className={`flex items-start gap-2 text-sm rounded-lg border p-2 cursor-pointer
                        ${answers[q.id] === alt ? 'border-violet-400 bg-violet-50' : 'hover:bg-slate-50'}`}>
                        <input type="radio" name={q.id} className="mt-1" checked={answers[q.id] === alt}
                               onChange={() => setAnswers(a => ({ ...a, [q.id]: alt }))}
                               data-testid={`aula-alt-${q.id}-${i}`} />
                        <span>{alt}</span>
                      </label>
                    ))}
                  </div>
                  {hints[q.id]?.text && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      <Lightbulb className="w-3 h-3 inline mr-1" />{hints[q.id].text}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => responder(q)} data-testid={`aula-responder-${q.id}`}>Responder</Button>
                    <Button size="sm" variant="outline" onClick={() => pedirDica(q)} data-testid={`aula-dica-${q.id}`}>
                      <Lightbulb className="w-4 h-4 mr-1" />Dica
                    </Button>
                  </div>
                  {feedback[q.id] && (
                    <div className={`text-sm rounded-lg p-2 ${feedback[q.id].correct ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}
                         data-testid={`aula-feedback-${q.id}`}>
                      {feedback[q.id].correct
                        ? <p><CheckCircle2 className="w-4 h-4 inline mr-1" />Correto! Domínio agora: {feedback[q.id].mastery}%</p>
                        : <p><XCircle className="w-4 h-4 inline mr-1" />Ainda não. Resposta: {feedback[q.id].correta}</p>}
                      {feedback[q.id].explicacao && <p className="mt-1 text-slate-700">{feedback[q.id].explicacao}</p>}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-violet-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Brain className="w-4 h-4 text-violet-600" />Tutor DNA</CardTitle>
              <p className="text-xs text-slate-500">Peça para explicar de outro jeito — o tutor ajuda sem entregar a resposta.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2 flex-wrap">
                {['Não entendi', 'Explique de outro jeito', 'Me dê um exemplo'].map(p => (
                  <Button key={p} size="sm" variant="outline" onClick={() => setTutorInput(p)} data-testid={`tutor-atalho-${p}`}>{p}</Button>
                ))}
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2" data-testid="tutor-conversa">
                {tutorMsgs.map((m, i) => (
                  <div key={i} className={`text-sm rounded-lg p-2.5 ${m.role === 'user' ? 'bg-violet-600 text-white ml-8' : 'bg-slate-100 text-slate-800 mr-8'}`}>
                    {clean(m.content)}
                  </div>
                ))}
                {tutorLoading && <p className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />O tutor está pensando…</p>}
              </div>
              <div className="flex gap-2">
                <Input value={tutorInput} onChange={(e) => setTutorInput(e.target.value)}
                       onKeyDown={(e) => e.key === 'Enter' && perguntarTutor()}
                       placeholder="Escreva sua dúvida" data-testid="tutor-input" />
                <Button onClick={perguntarTutor} disabled={tutorLoading} data-testid="tutor-enviar">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
